<script lang="ts">
	/**
	 * Entry-point modal for the Analyse Check flow.
	 * User chooses between automatic (AI) evaluation or manual evaluation.
	 * - Automatic: triggers file picker and upload flow.
	 * - Manual: continues to the existing SelectAnalyticProcedureModal flow.
	 */
	import { t } from '$i18n/i18n'
	import { fly } from 'svelte/transition'
	import { onDestroy } from 'svelte'
	import { modals } from 'svelte-modals'
	import aiUploadAnimation from '@/animations/ai_upload_animation.gif'
	import documentWithCheckMark from '@/images/document_with_checkmark.png'
	import { closeModalOnOutsideClick } from '../stores'
	import SelectableCard from '../molecules/SelectableCard.svelte'

	export let isOpen: boolean = true

	/** Called when user selects "Automatische Auswertung". Caller should close modal and open file picker. */
	export let onSelectAutomatic: () => void = () => {}
	/** Called when user selects "Manuelle Auswertung". Caller should close modal and open SelectAnalyticProcedureModal. */
	export let onSelectManual: () => void = () => {}

	let selectedOption: 'automatic' | 'manual' | null = null
	/** True while file picker is open: hide modal content but keep backdrop (blur). */
	let filePickerOpen = false

	let filePickerCloseCleanup: (() => void) | null = null
	let delayedShowModalId: ReturnType<typeof setTimeout> | null = null

	function stopWaitingForFile() {
		filePickerOpen = false
		selectedOption = null
		filePickerCloseCleanup?.()
		filePickerCloseCleanup = null
	}

	function handleSelectAutomatic() {
		selectedOption = 'automatic'
		filePickerOpen = true
		onSelectAutomatic()

		const delayMs = 300
		const timeoutId = setTimeout(() => {
			const onFocus = () => {
				// Delay so that if user selected a file, input "change" runs first and parent closes modal.
				delayedShowModalId = setTimeout(() => {
					delayedShowModalId = null
					if (filePickerOpen) stopWaitingForFile()
				}, 80)
			}
			window.addEventListener('focus', onFocus)
			filePickerCloseCleanup = () => {
				clearTimeout(timeoutId)
				window.removeEventListener('focus', onFocus)
				if (delayedShowModalId != null) {
					clearTimeout(delayedShowModalId)
					delayedShowModalId = null
				}
			}
		}, delayMs)
		filePickerCloseCleanup = () => clearTimeout(timeoutId)
	}

	onDestroy(() => stopWaitingForFile())

	function handleSelectManual() {
		selectedOption = 'manual'
		modals.close()
		$closeModalOnOutsideClick = true
		onSelectManual()
	}

	$: automaticTitle = $t('AnalysisCheck.entryModal.cardAutomatic.title')
	$: automaticDescription = $t('AnalysisCheck.entryModal.cardAutomatic.description')
	$: automaticProcessBadge = {
		text: $t('UI.badge.newFunctionality'),
		type: 'news',
		newsId: 'ai-analysis-check'
	}
	$: automaticOnHoverToolTip = $t('AnalysisCheck.entryModal.cardAutomatic.tooltip')
	$: manualTitle = $t('AnalysisCheck.entryModal.cardManual.title')
	$: manualDescription = $t('AnalysisCheck.entryModal.cardManual.description')
</script>

{#if isOpen}
	<div
		role="dialog"
		aria-labelledby="analysis-evaluation-modal-title"
		class="modal modal-backdrop"
		in:fly={{ y: -1000, duration: 400 }}
		out:fly={{ y: -1000, duration: 400 }}
	>
		<div class="contents">
			<header class="analysis-modal-header">
				<h1 id="analysis-evaluation-modal-title" class="analysis-modal-header__title">
					{@html $t('AnalysisCheck.entryModal.title')}
				</h1>
				<h4 class="analysis-modal-header__subtitle">
					{@html $t('AnalysisCheck.entryModal.subtitle')}
				</h4>
			</header>
			<div class="cards">
				<SelectableCard
					title={automaticTitle}
					description={automaticDescription}
					onHoverToolTip={automaticOnHoverToolTip}
					processBadge={automaticProcessBadge}
					imageSrc={aiUploadAnimation}
					imageAlt={automaticTitle}
					selected={selectedOption === 'automatic'}
					onSelect={handleSelectAutomatic}
				/>
				<SelectableCard
					title={manualTitle}
					description={manualDescription}
					imageSrc={documentWithCheckMark}
					imageAlt={manualTitle}
					selected={selectedOption === 'manual'}
					onSelect={handleSelectManual}
				/>
			</div>
		</div>

		{#if filePickerOpen}
			<div
				class="file-picker-overlay"
				role="button"
				tabindex="-1"
				on:click={stopWaitingForFile}
				on:keydown={(e) => e.key === 'Enter' && stopWaitingForFile()}
			></div>
		{/if}
	</div>
{/if}

<style lang="scss">
	@use '../../styles/modals';

	.modal {
		background: rgba(58, 58, 58, 0.4);
		pointer-events: all;
	}

	.contents {
		row-gap: 0.625rem;
		--content-padding: 2.25rem 1.5rem 2rem 2.25rem;
		width: 34.375rem;
		padding: 1.5rem;
	}

	.analysis-modal-header {
		width: 100%;
		flex-direction: column;
		align-items: flex-start;

		&__title {
			@include modals.roboto-font(normal, 500, 1.25rem, #000);
			margin: 0 0 0.5rem;
		}

		&__subtitle {
			@include modals.roboto-font(normal, 400, 0.875rem, #000);
			margin: 0;
		}
	}

	.cards {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 0.625rem;
	}
</style>
